import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { CheckCircle2, Circle, ArrowRight, X, Sparkles, RotateCcw, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

export default function GestionaleOnboarding() {
  const [data, setData] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  const fetchStatus = async () => {
    try {
      const r = await api.get("/gestionale/onboarding");
      setData(r.data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { fetchStatus(); }, []);

  const toggleStep = async (key) => {
    try {
      await api.post(`/gestionale/onboarding/${key}/toggle`);
      fetchStatus();
    } catch (e) {
      toast.error("Step non toggleabile manualmente");
    }
  };

  const dismiss = async () => {
    if (!window.confirm("Nascondere il setup wizard? Potrai riattivarlo dalle impostazioni.")) return;
    await api.post("/gestionale/onboarding/dismiss");
    fetchStatus();
  };

  const restore = async () => {
    await api.post("/gestionale/onboarding/restore");
    fetchStatus();
    toast.success("Wizard riattivato");
  };

  if (!data) return null;

  // If user dismissed → show only a small "Riattiva" link
  if (data.dismissed) {
    return (
      <div data-testid="onboarding-dismissed" className="mb-6 flex items-center justify-end">
        <button onClick={restore} data-testid="onboarding-restore" className="text-[10px] uppercase tracking-[0.25em] text-white/30 hover:text-[#C9A961] inline-flex items-center gap-1.5">
          <RotateCcw className="h-3 w-3" /> Riattiva setup wizard
        </button>
      </div>
    );
  }

  const allDone = data.completed === data.total;

  return (
    <div data-testid="onboarding-wizard" className="mb-12 bg-gradient-to-r from-[#C9A961]/10 via-black/40 to-[#C9A961]/5 border border-[#C9A961]/40 rounded-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 p-5 border-b border-[#C9A961]/20">
        <div className="flex items-center gap-3">
          <Sparkles className="h-4 w-4 text-[#C9A961]" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9A961] font-bold">Setup Wizard · Onboarding</div>
            <div className="text-sm text-white mt-0.5">
              {allDone ? <span className="italic">Setup completato. Il gestionale è operativo. ✦</span> : <>Primi 5 step per attivare il gestionale</>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setCollapsed(!collapsed)} data-testid="onboarding-collapse" className="text-white/40 hover:text-white" title={collapsed ? "Espandi" : "Riduci"}>
            {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
          </button>
          <button onClick={dismiss} data-testid="onboarding-dismiss" className="text-white/30 hover:text-white/80" title="Nascondi">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="px-5 pt-4">
        <div className="flex items-center justify-between mb-2 text-[10px] uppercase tracking-[0.25em]">
          <span className="text-white/60 font-bold">{data.completed} / {data.total} completati</span>
          <span className="text-[#C9A961] font-bold" data-testid="onboarding-progress-pct">{data.percentage}%</span>
        </div>
        <div className="h-1.5 bg-black/60 rounded-sm overflow-hidden">
          <div
            data-testid="onboarding-progress-bar"
            className="h-full bg-gradient-to-r from-[#C9A961] to-[#E2A03F] transition-all duration-700"
            style={{ width: `${data.percentage}%` }}
          />
        </div>
      </div>

      {!collapsed && (
        <div className="p-5 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {data.steps.map((s) => (
            <div
              key={s.key}
              data-testid={`onboarding-step-${s.key}`}
              className={`relative bg-black/40 border rounded-sm p-4 flex flex-col gap-2 transition-all
                ${s.done ? "border-[#C9A961]/50" : "border-white/10 hover:border-white/25"}`}
            >
              <div className="flex items-center justify-between">
                <div className={`text-2xl editorial-heading leading-none ${s.done ? "text-[#C9A961]" : "text-white/30"}`}>0{s.n}</div>
                {s.done
                  ? <CheckCircle2 className="h-5 w-5 text-[#C9A961]" />
                  : <Circle className="h-5 w-5 text-white/20" />}
              </div>
              <div className={`text-sm font-semibold leading-tight ${s.done ? "text-white" : "text-white/70"}`}>{s.title}</div>
              <p className="text-[10px] text-white/40 leading-relaxed flex-1">{s.desc}</p>
              {s.auto && s.current_count > 0 && (
                <div className="text-[10px] text-[#C9A961]/80 uppercase tracking-wider">
                  {s.current_count} {s.key === "supplier" ? "fornitori" : s.key === "import" ? "import" : "scan"} attivi
                </div>
              )}
              <div className="flex items-center justify-between pt-2 mt-auto border-t border-white/5">
                <Link
                  to={s.href}
                  data-testid={`onboarding-cta-${s.key}`}
                  className={`text-[10px] uppercase tracking-[0.2em] font-bold inline-flex items-center gap-1 ${s.done ? "text-white/50" : "text-[#C9A961]"} hover:text-white`}
                >
                  {s.done ? "Rivedi" : s.cta} <ArrowRight className="h-3 w-3" />
                </Link>
                {!s.auto && (
                  <button
                    onClick={() => toggleStep(s.key)}
                    data-testid={`onboarding-toggle-${s.key}`}
                    className="text-[9px] uppercase tracking-wider text-white/30 hover:text-[#C9A961]"
                    title="Toggle manuale"
                  >
                    {s.done ? "Annulla" : "Segna"}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
